import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";
import type { Session, User } from "@supabase/supabase-js";

function tagSentry(user: User | null) {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email ?? undefined });
  } else {
    Sentry.setUser(null);
  }
}

export function useUser() {
  const [state, setState] = useState<{
    user: User | null;
    session: Session | null;
    loading: boolean;
  }>({ user: null, session: null, loading: true });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      const user = session?.user ?? null;
      tagSentry(user);
      setState({ user, session, loading: false });
    });
    // getSession() may attempt a background token refresh against
    // /auth/v1/token if a stale session exists in localStorage; if the refresh
    // token has been revoked (one-shot tokens) the call rejects. Swallow it —
    // the SDK clears the bad session internally and the listener above will
    // fire with `session: null`. Without the catch, the rejection bubbles up
    // as an unhandled promise rejection on routes that mount Header for anon
    // visitors who happen to have leftover Supabase storage from an earlier
    // session.
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        const user = session?.user ?? null;
        tagSentry(user);
        setState({ user, session, loading: false });
      })
      .catch(() => {
        tagSentry(null);
        setState({ user: null, session: null, loading: false });
      });
    return () => subscription.unsubscribe();
  }, []);

  return state;
}
